import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';

import {
    distance,
    formatLength,
    getBounds,
    moveJunction,
    roundPoint,
    samePoint,
    snapPoint,
    storeyPoints,
    wallAxes,
    wallLength,
    wallOutline
} from 'app/modules/ifc-editor/utils/wallGeometry';

import type {
    BuildingSpecification,
    EditorSelection,
    PlanPoint,
    WallSpecification
} from 'app/modules/ifc-editor/types/buildingSpecification';

interface WallPlanViewProps {
    specification: BuildingSpecification;
    storey: string;
    selection: EditorSelection;
    showDimensions: boolean;
    /** Changing this value re-fits the view to the storey. */
    fitKey: string;
    onSelect: (selection: EditorSelection) => void;
    onApply: (update: (current: BuildingSpecification) => BuildingSpecification, transient?: boolean) => void;
    onCheckpoint: () => void;
}

interface ViewBox {
    x: number;
    y: number;
    width: number;
    height: number;
}

type Drag =
    | { kind: 'pan'; startClient: PlanPoint; startView: ViewBox }
    | { kind: 'junction'; base: BuildingSpecification; from: PlanPoint; anchor: PlanPoint; candidates: PlanPoint[] }
    | { kind: 'wall'; base: BuildingSpecification; wall: WallSpecification; startWorld: PlanPoint }
    | { kind: 'slabVertex'; base: BuildingSpecification; slabIndex: number; vertexIndex: number };

const DRAG_THRESHOLD_PX = 3;
const ACCENT = '#2563eb';
const OPENING_COLORS = { Door: '#ea580c', Window: '#0284c7', Opening: '#7c3aed' } as const;

/** Plan coordinates have Y up; SVG has Y down. */
const toSvg = (point: PlanPoint) => ({ x: point.x, y: -point.y });
const pointsAttribute = (points: PlanPoint[]) => points.map((point) => `${point.x},${-point.y}`).join(' ');

/** Keeps rotated labels upright. */
const readableAngle = (dx: number, dy: number) => {
    let angle = (Math.atan2(-dy, dx) * 180) / Math.PI;
    if (angle > 90) {
        angle -= 180;
    } else if (angle <= -90) {
        angle += 180;
    }
    return angle;
};

const fitView = (points: PlanPoint[]): ViewBox => {
    const bounds = getBounds(points);
    const width = Math.max(bounds.maxX - bounds.minX, 1);
    const height = Math.max(bounds.maxY - bounds.minY, 1);
    const margin = Math.max(width, height) * 0.15 + 1;
    return {
        x: bounds.minX - margin,
        y: -bounds.maxY - margin,
        width: width + 2 * margin,
        height: height + 2 * margin
    };
};

/** Interactive, dimensioned 2D plan of one storey. */
export const WallPlanView = ({
    specification,
    storey,
    selection,
    showDimensions,
    fitKey,
    onSelect,
    onApply,
    onCheckpoint
}: WallPlanViewProps) => {
    const svgRef = useRef<SVGSVGElement | null>(null);
    const dragRef = useRef<{ drag: Drag; startClient: PlanPoint; active: boolean } | null>(null);
    const [size, setSize] = useState({ width: 800, height: 600 });
    const [view, setView] = useState<ViewBox>(() =>
        fitView(
            storeyPoints(specification, storey).concat(specification.walls.flatMap((wall) => [wall.start, wall.end]))
        )
    );

    const specificationRef = useRef(specification);
    useEffect(() => {
        specificationRef.current = specification;
    }, [specification]);

    useEffect(() => {
        const points = storeyPoints(specificationRef.current, storey);
        setView(
            fitView(
                points.length > 0 ? points : specificationRef.current.walls.flatMap((wall) => [wall.start, wall.end])
            )
        );
    }, [fitKey, storey]);

    useEffect(() => {
        const svg = svgRef.current;
        if (!svg) {
            return;
        }
        const observer = new ResizeObserver(([entry]) =>
            setSize({ width: entry.contentRect.width || 1, height: entry.contentRect.height || 1 })
        );
        observer.observe(svg);
        return () => observer.disconnect();
    }, []);

    /** The fitted view expanded to the panel's aspect ratio, so the grid fills the panel without letterboxing. */
    const shown = useMemo<ViewBox>(() => {
        const aspect = size.width / size.height;
        if (view.width / view.height > aspect) {
            const height = view.width / aspect;
            return { ...view, y: view.y - (height - view.height) / 2, height };
        }
        const width = view.height * aspect;
        return { ...view, x: view.x - (width - view.width) / 2, width };
    }, [size, view]);

    /** Metres per screen pixel, so labels and handles keep a constant on-screen size. */
    const px = shown.width / size.width;

    const toWorld = useCallback((clientX: number, clientY: number): PlanPoint => {
        const matrix = svgRef.current?.getScreenCTM();
        if (!matrix) {
            return { x: 0, y: 0 };
        }
        const point = new DOMPoint(clientX, clientY).matrixTransform(matrix.inverse());
        return { x: point.x, y: -point.y };
    }, []);

    useEffect(() => {
        const svg = svgRef.current;
        if (!svg) {
            return;
        }
        // Native listener: React registers wheel handlers as passive, which cannot prevent page scrolling.
        const onWheel = (event: WheelEvent) => {
            event.preventDefault();
            const world = toWorld(event.clientX, event.clientY);
            const factor = event.deltaY > 0 ? 1.15 : 1 / 1.15;
            setView((current) => {
                const cursor = toSvg(world);
                return {
                    x: cursor.x - (cursor.x - current.x) * factor,
                    y: cursor.y - (cursor.y - current.y) * factor,
                    width: current.width * factor,
                    height: current.height * factor
                };
            });
        };
        svg.addEventListener('wheel', onWheel, { passive: false });
        return () => svg.removeEventListener('wheel', onWheel);
    }, [toWorld]);

    const storeyWalls = useMemo(
        () => specification.walls.map((wall, index) => ({ wall, index })).filter(({ wall }) => wall.storey === storey),
        [specification.walls, storey]
    );
    const centroid = useMemo(() => {
        const points = storeyWalls.flatMap(({ wall }) => [wall.start, wall.end]);
        return points.length === 0
            ? { x: 0, y: 0 }
            : {
                  x: points.reduce((sum, point) => sum + point.x, 0) / points.length,
                  y: points.reduce((sum, point) => sum + point.y, 0) / points.length
              };
    }, [storeyWalls]);

    /** Delegated: elements only carry `data-role`/`data-index`, so no ref-using closures are created per element. */
    const handlePointerDown = (event: ReactPointerEvent<SVGSVGElement>) => {
        const target = event.target as SVGElement;
        const role = target.dataset.role;
        const index = Number(target.dataset.index);
        const world = toWorld(event.clientX, event.clientY);
        let drag: Drag | null = null;
        if (role === 'background') {
            drag = { kind: 'pan', startClient: { x: event.clientX, y: event.clientY }, startView: view };
        } else if (role === 'wall') {
            onSelect({ type: 'wall', index });
            drag = { kind: 'wall', base: specification, wall: specification.walls[index], startWorld: world };
        } else if (role === 'slab') {
            onSelect({ type: 'slab', index });
        } else if (role === 'wall-handle' && selection?.type === 'wall') {
            const wall = specification.walls[selection.index];
            const end = target.dataset.end === 'start' ? 'start' : 'end';
            drag = {
                kind: 'junction',
                base: specification,
                from: wall[end],
                anchor: end === 'start' ? wall.end : wall.start,
                candidates: specification.walls
                    .filter((other) => other.storey === storey)
                    .flatMap((other) => [other.start, other.end])
                    .filter((point) => !samePoint(point, wall[end]))
            };
        } else if (role === 'slab-vertex' && selection?.type === 'slab') {
            drag = { kind: 'slabVertex', base: specification, slabIndex: selection.index, vertexIndex: index };
        }
        if (drag) {
            event.currentTarget.setPointerCapture(event.pointerId);
            dragRef.current = { drag, startClient: { x: event.clientX, y: event.clientY }, active: false };
        }
    };

    const handlePointerMove = (event: ReactPointerEvent) => {
        const state = dragRef.current;
        if (!state) {
            return;
        }
        const { drag } = state;
        if (!state.active) {
            if (distance(state.startClient, { x: event.clientX, y: event.clientY }) < DRAG_THRESHOLD_PX) {
                return;
            }
            state.active = true;
            if (drag.kind !== 'pan') {
                onCheckpoint();
            }
        }

        if (drag.kind === 'pan') {
            setView({
                ...drag.startView,
                x: drag.startView.x - (event.clientX - drag.startClient.x) * px,
                y: drag.startView.y - (event.clientY - drag.startClient.y) * px
            });
            return;
        }
        const world = toWorld(event.clientX, event.clientY);
        if (drag.kind === 'junction') {
            const target = snapPoint(world, drag.candidates, drag.anchor, 10 * px);
            onApply(() => moveJunction(drag.base, storey, drag.from, target), true);
        } else if (drag.kind === 'wall') {
            // Walls move parallel; connected walls stretch to stay joined.
            const { nx, ny } = wallAxes(drag.wall);
            const offset =
                Math.round(((world.x - drag.startWorld.x) * nx + (world.y - drag.startWorld.y) * ny) * 1000) / 1000;
            const shift = (point: PlanPoint) => ({ x: point.x + nx * offset, y: point.y + ny * offset });
            onApply(() => {
                const movedStart = moveJunction(drag.base, storey, drag.wall.start, shift(drag.wall.start));
                return moveJunction(movedStart, storey, drag.wall.end, shift(drag.wall.end));
            }, true);
        } else {
            const target = roundPoint(world);
            onApply(
                () => ({
                    ...drag.base,
                    slabs: drag.base.slabs.map((slab, slabIndex) =>
                        slabIndex === drag.slabIndex
                            ? {
                                  ...slab,
                                  outline: slab.outline.map((point, vertexIndex) =>
                                      vertexIndex === drag.vertexIndex ? target : point
                                  )
                              }
                            : slab
                    )
                }),
                true
            );
        }
    };

    const handlePointerUp = (event: ReactPointerEvent) => {
        const state = dragRef.current;
        dragRef.current = null;
        if (state?.drag.kind === 'pan' && !state.active) {
            onSelect(null);
        }
        (event.currentTarget as Element).releasePointerCapture?.(event.pointerId);
    };

    const gridStep = Math.pow(10, Math.ceil(Math.log10(px * 15)));
    const selectedWall = selection?.type === 'wall' ? specification.walls[selection.index] : undefined;
    const selectedSlab = selection?.type === 'slab' ? specification.slabs[selection.index] : undefined;
    const fontSize = 11 * px;

    const renderDimension = (wall: WallSpecification, key: string, emphasized: boolean) => {
        const length = wallLength(wall);
        if (length === 0) {
            return null;
        }
        const { ux, uy, nx: leftX, ny: leftY } = wallAxes(wall);
        const mid = { x: (wall.start.x + wall.end.x) / 2, y: (wall.start.y + wall.end.y) / 2 };
        // Dimension lines go on the outside of the building.
        const outward = (mid.x - centroid.x) * leftX + (mid.y - centroid.y) * leftY >= 0 ? 1 : -1;
        const nx = leftX * outward;
        const ny = leftY * outward;
        const offset = wall.thickness / 2 + 16 * px;
        const at = (point: PlanPoint, distanceFromAxis: number) =>
            toSvg({ x: point.x + nx * distanceFromAxis, y: point.y + ny * distanceFromAxis });
        const a = at(wall.start, offset);
        const b = at(wall.end, offset);
        const label = at(mid, offset + 7 * px);
        const tick = 4 * px;
        const color = emphasized ? ACCENT : '#475569';
        return (
            <g key={key} pointerEvents="none">
                {[wall.start, wall.end].map((point, index) => {
                    const from = at(point, wall.thickness / 2 + 2 * px);
                    const to = at(point, offset + 3 * px);
                    return (
                        <line
                            key={index}
                            stroke={color}
                            strokeWidth={1}
                            vectorEffect="non-scaling-stroke"
                            x1={from.x}
                            x2={to.x}
                            y1={from.y}
                            y2={to.y}
                        />
                    );
                })}
                <line
                    stroke={color}
                    strokeWidth={1}
                    vectorEffect="non-scaling-stroke"
                    x1={a.x}
                    x2={b.x}
                    y1={a.y}
                    y2={b.y}
                />
                {[a, b].map((point, index) => (
                    <line
                        key={`tick-${index}`}
                        stroke={color}
                        strokeWidth={1.5}
                        vectorEffect="non-scaling-stroke"
                        x1={point.x - (ux - uy) * tick * 0.7}
                        x2={point.x + (ux - uy) * tick * 0.7}
                        y1={point.y + (uy + ux) * tick * 0.7}
                        y2={point.y - (uy + ux) * tick * 0.7}
                    />
                ))}
                <text
                    data-testid={`wall-dimension-${wall.id}`}
                    dominantBaseline="middle"
                    fill={color}
                    fontSize={fontSize}
                    fontWeight={emphasized ? 600 : 400}
                    paintOrder="stroke"
                    stroke="#ffffff"
                    strokeWidth={3 * px}
                    textAnchor="middle"
                    transform={`rotate(${readableAngle(ux, uy)} ${label.x} ${label.y})`}
                    x={label.x}
                    y={label.y}
                >
                    {formatLength(length)}
                </text>
            </g>
        );
    };

    return (
        <svg
            ref={svgRef}
            aria-label="Wall plan"
            className="h-full w-full touch-none select-none bg-white"
            data-testid="wall-plan"
            preserveAspectRatio="xMidYMid meet"
            viewBox={`${shown.x} ${shown.y} ${shown.width} ${shown.height}`}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
        >
            <defs>
                <pattern height={gridStep} id="wall-plan-grid" patternUnits="userSpaceOnUse" width={gridStep}>
                    <path
                        d={`M ${gridStep} 0 L 0 0 0 ${gridStep}`}
                        fill="none"
                        stroke="#eef2f7"
                        strokeWidth={1}
                        vectorEffect="non-scaling-stroke"
                    />
                </pattern>
            </defs>
            <rect
                cursor="grab"
                data-role="background"
                fill="url(#wall-plan-grid)"
                height={shown.height}
                width={shown.width}
                x={shown.x}
                y={shown.y}
            />

            {specification.walls
                .filter((wall) => wall.storey !== storey)
                .map((wall, index) => (
                    <polygon
                        key={`ghost-${index}`}
                        fill="none"
                        pointerEvents="none"
                        points={pointsAttribute(wallOutline(wall))}
                        stroke="#cbd5e1"
                        strokeDasharray="4 3"
                        vectorEffect="non-scaling-stroke"
                    />
                ))}

            {specification.slabs.map((slab, index) =>
                slab.storey !== storey ? null : (
                    <polygon
                        key={`slab-${index}`}
                        cursor="pointer"
                        data-index={index}
                        data-role="slab"
                        data-testid={`slab-${index}`}
                        fill={slab.kind === 'Foundation' ? 'rgba(120,113,108,0.16)' : 'rgba(148,163,184,0.14)'}
                        points={pointsAttribute(slab.outline)}
                        stroke={selection?.type === 'slab' && selection.index === index ? ACCENT : '#64748b'}
                        strokeDasharray="6 4"
                        strokeWidth={selection?.type === 'slab' && selection.index === index ? 2 : 1}
                        vectorEffect="non-scaling-stroke"
                    />
                )
            )}

            {storeyWalls.map(({ wall, index }) => {
                const isSelected = selection?.type === 'wall' && selection.index === index;
                return (
                    <g key={`wall-${index}`}>
                        <polygon
                            cursor={isSelected ? 'move' : 'pointer'}
                            data-index={index}
                            data-role="wall"
                            data-testid={`wall-${wall.id}`}
                            fill={isSelected ? 'rgba(37,99,235,0.35)' : '#cbd5e1'}
                            points={pointsAttribute(wallOutline(wall))}
                            stroke={isSelected ? ACCENT : '#334155'}
                            strokeWidth={isSelected ? 2 : 1}
                            vectorEffect="non-scaling-stroke"
                        />
                        {wall.openings.map((opening, openingIndex) => {
                            const { ux, uy } = wallAxes(wall);
                            const at = (along: number) => ({
                                x: wall.start.x + ux * along,
                                y: wall.start.y + uy * along
                            });
                            const segment: WallSpecification = {
                                ...wall,
                                start: at(opening.offset),
                                end: at(opening.offset + opening.width),
                                thickness: wall.thickness * 1.02
                            };
                            return (
                                <polygon
                                    key={openingIndex}
                                    fill="#ffffff"
                                    pointerEvents="none"
                                    points={pointsAttribute(wallOutline(segment))}
                                    stroke={OPENING_COLORS[opening.kind]}
                                    strokeWidth={1.5}
                                    vectorEffect="non-scaling-stroke"
                                />
                            );
                        })}
                    </g>
                );
            })}

            {storeyWalls.map(({ wall, index }) =>
                showDimensions || (selection?.type === 'wall' && selection.index === index)
                    ? renderDimension(
                          wall,
                          `dimension-${index}`,
                          selection?.type === 'wall' && selection.index === index
                      )
                    : null
            )}

            {selectedSlab?.outline.map((point, vertexIndex) => {
                const next = selectedSlab.outline[(vertexIndex + 1) % selectedSlab.outline.length];
                const mid = toSvg({ x: (point.x + next.x) / 2, y: (point.y + next.y) / 2 });
                return (
                    <text
                        key={`slab-edge-${vertexIndex}`}
                        dominantBaseline="middle"
                        fill={ACCENT}
                        fontSize={fontSize}
                        paintOrder="stroke"
                        pointerEvents="none"
                        stroke="#ffffff"
                        strokeWidth={3 * px}
                        textAnchor="middle"
                        transform={`rotate(${readableAngle(next.x - point.x, next.y - point.y)} ${mid.x} ${mid.y})`}
                        x={mid.x}
                        y={mid.y}
                    >
                        {formatLength(distance(point, next))}
                    </text>
                );
            })}
            {selection?.type === 'slab' &&
                selectedSlab?.outline.map((point, vertexIndex) => {
                    const position = toSvg(point);
                    return (
                        <circle
                            key={`slab-vertex-${vertexIndex}`}
                            cursor="move"
                            cx={position.x}
                            cy={position.y}
                            data-index={vertexIndex}
                            data-role="slab-vertex"
                            fill="#ffffff"
                            r={5 * px}
                            stroke={ACCENT}
                            strokeWidth={2}
                            vectorEffect="non-scaling-stroke"
                        />
                    );
                })}

            {selectedWall &&
                (['start', 'end'] as const).map((end) => {
                    const position = toSvg(selectedWall[end]);
                    return (
                        <circle
                            key={end}
                            cursor="crosshair"
                            cx={position.x}
                            cy={position.y}
                            data-end={end}
                            data-role="wall-handle"
                            data-testid={`wall-handle-${end}`}
                            fill="#ffffff"
                            r={6 * px}
                            stroke={ACCENT}
                            strokeWidth={2}
                            vectorEffect="non-scaling-stroke"
                        />
                    );
                })}
        </svg>
    );
};
